import { useCallback, useEffect, useState } from "react";
import { Loader2, Upload, Check, Ban, Download, FileStack, PlugZap, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi, opsOpen, opsDownload } from "../../lib/api";

const TONE = {
  filed: "text-emerald-700", generated: "text-sky-700", on_file: "text-sky-700",
  waived: "text-slate-500", not_applicable: "text-slate-400", pending: "text-rose-700",
};

/** FIN-4 — everything that travels with the application: the requirement checklist for this
 *  period, the stored-material exceptions, and the platform export. */
export default function PackagePanel({ project, appId, onChanged }) {
  const [pkg, setPkg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stored, setStored] = useState({ status: "", expected_at: "", note: "" });
  const [ref, setRef] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await opsApi.payAppPackage(project.id, appId);
      setPkg(d);
      setStored({ status: d.stored.status, expected_at: d.stored.expected_at || "",
                  note: d.stored.note || "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load the package"); }
  }, [project.id, appId]);
  useEffect(() => { load(); }, [load]);

  const setItem = async (key, status, note = "") => {
    setBusy(true);
    try { setPkg(await opsApi.payAppPackageItem(project.id, appId, key, { status, note })); }
    catch (e) { toast.error(e?.response?.data?.detail || "That could not be recorded"); }
    finally { setBusy(false); }
  };

  const upload = async (key, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("key", key);
      form.append("file", file);
      setPkg(await opsApi.payAppPackageUpload(project.id, appId, form));
      toast.success("Filed");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That file could not be filed"); }
    finally { setBusy(false); }
  };

  const saveStored = async () => {
    setBusy(true);
    try {
      setPkg(await opsApi.payAppStoredMaterial(project.id, appId, stored));
      toast.success("Stored material status recorded");
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be recorded"); }
    finally { setBusy(false); }
  };

  const markUploaded = async () => {
    setBusy(true);
    try {
      await opsApi.payAppMarkUploaded(project.id, appId, { draw_number: ref });
      toast.success("Platform upload recorded");
      setRef("");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be recorded"); }
    finally { setBusy(false); }
  };

  if (!pkg) {
    return <div className="flex items-center gap-2 py-6 text-xs text-slate-500">
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading the billing package…
    </div>;
  }
  const base = `/projects/${project.id}/invoicing/pay-applications/${appId}`;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ops-package">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-sm font-semibold text-slate-900">Billing package</h3>
        <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${
          pkg.complete ? "border-emerald-200 bg-emerald-50 text-emerald-700"
            : "border-rose-200 bg-rose-50 text-rose-700"}`}
              data-testid="ops-package-headline">{pkg.headline}</span>
        <span className="text-xs text-slate-500">
          {pkg.platform_label} · {pkg.export_label}
        </span>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" size="sm" data-testid="ops-package-pdf"
                  onClick={() => opsOpen(`${base}/package/pdf`)
                    .catch(() => toast.error("That package could not be assembled"))}>
            <FileStack className="mr-1.5 h-3.5 w-3.5" /> One PDF
          </Button>
          <Button variant="outline" size="sm" data-testid="ops-package-export"
                  onClick={() => opsDownload(`${base}/export`, `${pkg.number}-export.csv`)
                    .catch(() => toast.error("That export could not be built"))}>
            <Download className="mr-1.5 h-3.5 w-3.5" /> {pkg.export_label}
          </Button>
          <Button variant="outline" size="sm" data-testid="ops-package-payroll"
                  onClick={() => opsDownload(`${base}/certified-payroll`,
                                             `${pkg.number}-certified-payroll.csv`)
                    .catch(() => toast.error("That export could not be built"))}>
            Certified payroll
          </Button>
          <Button variant="outline" size="sm" disabled={busy} data-testid="ops-package-file"
                  onClick={async () => {
                    setBusy(true);
                    try { await opsApi.payAppFilePackage(project.id, appId); await load();
                          toast.success("Filed to Documents › Billing"); }
                    catch (e) { toast.error(e?.response?.data?.detail || "Could not file it"); }
                    finally { setBusy(false); }
                  }}>
            File to Documents
          </Button>
        </div>
      </div>

      {pkg.blocking?.length > 0 && (
        <p className="mt-2 flex items-start gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
           data-testid="ops-package-blocking">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Hard stop — this cannot be submitted until {pkg.blocking.join(", ")} is filed.
        </p>
      )}

      <div className="mt-3 overflow-hidden rounded-lg border border-slate-200">
        <table className="w-full text-xs" data-testid="ops-package-items">
          <tbody className="divide-y divide-slate-100">
            {pkg.items.map((i) => (
              <tr key={i.key} data-testid={`ops-package-item-${i.key}`}>
                <td className="px-3 py-1.5">
                  <span className="font-medium text-slate-800">{i.label}</span>
                  {i.hard_stop && (
                    <span className="ml-1.5 rounded bg-rose-100 px-1 text-[10px] font-medium text-rose-700">
                      hard stop
                    </span>
                  )}
                  {i.requires_notarization && (
                    <span className="ml-1.5 text-[10px] text-slate-400">notarised</span>
                  )}
                  {i.note && <p className="text-[11px] italic text-slate-500">{i.note}</p>}
                </td>
                <td className={`px-3 py-1.5 ${TONE[i.status] || "text-slate-600"}`}>
                  {i.status_label}
                  {i.generated_as && i.status === "generated" && (
                    <span className="text-slate-400"> — {i.generated_as}</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right">
                  <div className="flex justify-end gap-1">
                    <label className="cursor-pointer rounded border border-slate-200 px-1.5 py-0.5 text-[11px] text-slate-600 hover:border-slate-400">
                      <Upload className="inline h-3 w-3" />
                      <input type="file" className="hidden" disabled={busy}
                             data-testid={`ops-package-upload-${i.key}`}
                             onChange={(e) => upload(i.key, e.target.files?.[0])} />
                    </label>
                    <button onClick={() => setItem(i.key, "not_applicable")} disabled={busy}
                            data-testid={`ops-package-na-${i.key}`}
                            className="rounded border border-slate-200 px-1.5 py-0.5 text-[11px] text-slate-500 hover:border-slate-400">
                      N/A
                    </button>
                    <button disabled={busy} data-testid={`ops-package-waive-${i.key}`}
                            onClick={() => {
                              const why = window.prompt(`Why is ${i.label} waived this period?`);
                              if (why) setItem(i.key, "waived", why);
                            }}
                            className="rounded border border-slate-200 px-1.5 py-0.5 text-[11px] text-slate-500 hover:border-slate-400">
                      <Ban className="inline h-3 w-3" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {pkg.items.length === 0 && (
              <tr><td className="px-3 py-4 text-center text-slate-500">
                No billing requirements are set for this job — set them in Billing Setup.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pkg.items.some((i) => i.stored_material) && (
        <div className="mt-3 rounded-lg border border-slate-200 p-3" data-testid="ops-package-stored">
          <p className="text-xs font-semibold text-slate-800">Stored material documentation</p>
          <p className="mt-0.5 text-[11px] text-slate-500">
            A package can still go out on a pending status — the note below goes with it and a
            follow-up is raised on the expected date.
          </p>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <label className="text-[11px]">
              <span className="block text-slate-500">Status</span>
              <select value={stored.status} data-testid="ops-package-stored-status"
                      onChange={(e) => setStored((p) => ({ ...p, status: e.target.value }))}
                      className="mt-0.5 h-8 rounded-md border border-slate-200 px-2 text-xs">
                {pkg.stored.states.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </label>
            <label className="text-[11px]">
              <span className="block text-slate-500">Expected</span>
              <Input type="date" value={stored.expected_at} className="mt-0.5 h-8 w-36 text-xs"
                     data-testid="ops-package-stored-date"
                     onChange={(e) => setStored((p) => ({ ...p, expected_at: e.target.value }))} />
            </label>
            <Button size="sm" onClick={saveStored} disabled={busy}
                    data-testid="ops-package-stored-save">Record</Button>
          </div>
          {pkg.stored.auto_note && (
            <p className="mt-2 rounded bg-slate-50 px-2 py-1 text-[11px] italic text-slate-600"
               data-testid="ops-package-stored-note">{pkg.stored.auto_note}</p>
          )}
        </div>
      )}

      {["textura", "gcpay", "procore"].includes(pkg.platform) && (
        <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 p-3">
          <label className="text-[11px]">
            <span className="block text-slate-500">
              {pkg.platform === "textura" ? "Textura draw #" : "Platform reference"} once uploaded
            </span>
            <Input value={ref} onChange={(e) => setRef(e.target.value)}
                   className="mt-0.5 h-8 w-48 text-xs" data-testid="ops-package-ref" />
          </label>
          <Button size="sm" variant="outline" onClick={markUploaded} disabled={busy || !ref.trim()}
                  data-testid="ops-package-mark-uploaded">
            <Check className="mr-1.5 h-3 w-3" /> Record the upload
          </Button>
        </div>
      )}

      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
           data-testid="ops-package-not-wired">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-900">
          <PlugZap className="h-3 w-3" /> Not connected yet
        </p>
        {pkg.not_wired.map((n, i) => (
          <p key={i} className="text-[11px] text-amber-900">· {n}</p>
        ))}
      </div>
    </section>
  );
}
